// The integrations between systems (docs/systems.md "Integrations"; the cloud's integrations.ts) as Settings → Systems
// shows them: per system what comes in and goes out (the cards), and the view of the system map. Pure functions: the
// view in the URL and the split per system (the map's layout is lib/systemlayout.ts).
import type { Integration } from './api.ts';

// ---------------------------------------------------------------- the view in the URL

export type SystemsMode = 'cards' | 'map';
export interface IntegrationsView {
  /** Cards (the default) or the system map: a history step each. */
  mode: SystemsMode;
  /** The arc selected on the map ("ERP>WMS"), replaced as it changes. */
  arc: string | null;
  /** The card asked for (from the map), scrolled to and marked. */
  at: string | null;
}

export function readIntegrationsView(p: URLSearchParams): IntegrationsView {
  const mode = p.get('view') === 'map' ? 'map' : 'cards';
  const arc = p.get('arc');
  return { mode, arc: mode === 'map' && arc && /^[^>]+>[^>]+$/.test(arc) ? arc : null, at: mode === 'cards' ? p.get('at') || null : null };
}

/** The URL with a change of the view: what belongs to the other mode goes; defaults leave the URL. */
export function withIntegrationsView(p: URLSearchParams, patch: Partial<IntegrationsView>): URLSearchParams {
  const v = { ...readIntegrationsView(p), ...patch };
  const out = new URLSearchParams(p);
  if (v.mode === 'map') out.set('view', 'map'); else out.delete('view');
  if (v.mode === 'map' && v.arc) out.set('arc', v.arc); else out.delete('arc');
  if (v.mode === 'cards' && v.at) out.set('at', v.at); else out.delete('at');
  return out;
}

export const arcKey = (from: string, to: string) => `${from}>${to}`;

// ---------------------------------------------------------------- per system

export interface SystemIntegrations {
  /** Data coming in: from another system (`from` set) or written by a process that reads nothing logged (`from` null). */
  in: Integration[];
  /** Data going out: to another system (`to` set) or read by a process that writes nothing logged (`to` null). */
  out: Integration[];
  /** Processes reading and writing within the system. */
  within: Integration[];
}

const order = (a: Integration, b: Integration) => rank(a) - rank(b) || b.runs - a.runs || a.process.localeCompare(b.process);
const rank = (i: Integration) => (i.status === 'failing' ? 0 : 1);

/** What comes into a system and goes out of it, the failing first, then the busiest. */
export function integrationsOf(list: readonly Integration[], code: string): SystemIntegrations {
  const out: SystemIntegrations = { in: [], out: [], within: [] };
  for (const i of list) {
    if (i.from === code && i.to === code) out.within.push(i);
    else if (i.to === code) out.in.push(i);
    else if (i.from === code) out.out.push(i);
  }
  out.in.sort(order);
  out.out.sort(order);
  out.within.sort(order);
  return out;
}

/** The query of the data search for the documents of a process. */
export const processQuery = (process: string) => `@process:"${process.replace(/"/g, '')}"`;

/** "every 5 min", "hourly", "daily", "every 2.5 h", or null. */
export function cadenceLabel(ms: number | null): string | null {
  if (!ms || ms <= 0) return null;
  const MIN = 60_000;
  const near = (x: number, unit: number) => Math.abs(x - unit) <= unit * 0.1;
  if (near(ms, 60 * MIN)) return 'hourly';
  if (near(ms, 24 * 60 * MIN)) return 'daily';
  if (near(ms, 7 * 24 * 60 * MIN)) return 'weekly';
  if (ms < MIN) return `every ${Math.max(1, Math.round(ms / 1000))} s`;
  if (ms < 90 * MIN) return `every ${Math.round(ms / MIN)} min`;
  if (ms < 36 * 60 * MIN) return `every ${+(ms / (60 * MIN)).toFixed(1)} h`;
  return `every ${+(ms / (24 * 60 * MIN)).toFixed(1)} d`;
}

// The system map's layout: lib/systemlayout.ts.
